import { useCallback, useEffect, useMemo, useState } from "react";
import { hasRole, userRoles } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { askDeductionStart } from "@/lib/loanDeduction";
import {
    Check, X, Ban, Inbox, CalendarDays, HandCoins, Receipt, Loader2,
    ShieldCheck, Building2, Wallet, FileSignature, Send,
} from "lucide-react";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

const LEAVE_PILL = {
    pending: { label: "Awaiting HR", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    hr_verified: { label: "HR Verified", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

const LOAN_PILL = {
    pending: { label: "New request", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    accountant_verified: { label: "Accountant verified", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    dept_approved: { label: "Dept approved", cls: "bg-violet-50 text-violet-700 border-violet-200" },
    finance_approved: { label: "Finance approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    approved: { label: "Finance approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

const LOAN_ACTION_LABEL = {
    accountant_verify: { label: "Verify (Accountant)", icon: ShieldCheck, cls: "border-sky-400 text-sky-700 hover:bg-sky-50" },
    dept_approve: { label: "Dept approve", icon: Building2, cls: "border-violet-400 text-violet-700 hover:bg-violet-50" },
    finance_approve: { label: "Finance approve", icon: Wallet, cls: "border-blue-500 text-blue-700 hover:bg-blue-50" },
    disburse: { label: "Disburse", icon: Send, cls: "border-emerald-500 text-emerald-700 hover:bg-emerald-50" },
    reject: { label: "Reject", icon: Ban, cls: "border-red-400 text-red-700 hover:bg-red-50" },
};

export default function PendingApprovals() {
    const { user } = useAuth();
    const { confirm, prompt } = useConfirm();
    const role = user?.role || "";
    const [tab, setTab] = useState("leaves");
    const [data, setData] = useState({ leaves: [], loans: [], rent_receipts: [], totals: {} });
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/pending-approvals");
            setData(r.data);
        } catch (e) {
            toast.error("Failed to load pending approvals");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // --- Leave actions ---
    const leaveAction = async (lv, action) => {
        let note = "";
        if (action === "reject") {
            const result = await prompt({
                title: `Reject ${lv.employee_name}'s leave?`,
                description: "Optionally share why so the employee can correct and resubmit.",
                placeholder: "e.g. Project deadline conflict",
                multiline: true,
                confirmLabel: "Reject leave",
            });
            if (result === null) return;
            note = result;
        }
        setBusyId(lv.id);
        try {
            await api.put(`/leaves/${lv.id}`, { action, note });
            toast.success(`Leave ${action === "hr_verify" ? "verified" : action + "d"}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        } finally {
            setBusyId("");
        }
    };

    // --- Loan actions ---
    const loanAction = async (loan, action) => {
        let note = "";
        let startPeriod;
        if (action === "reject") {
            const r = await prompt({
                title: `Reject ${loan.employee_name}'s ${loan.type || "loan"}?`,
                description: "The reason will be emailed to the employee.",
                placeholder: "e.g. Recent loan still outstanding",
                multiline: true,
                confirmLabel: "Reject request",
            });
            if (r === null) return;
            note = r;
        } else if (action === "disburse") {
            startPeriod = await askDeductionStart(prompt, loan, fmt);
            if (!startPeriod) return;
        } else if (action === "finance_approve") {
            const ok = await confirm({
                title: "Give final Finance approval?",
                description: `${loan.employee_name} · ${loan.type || "loan"} · ${fmt(loan.amount)}. This builds the installment schedule and notifies the employee.`,
                confirmLabel: "Approve",
            });
            if (!ok) return;
        }
        setBusyId(loan.id);
        try {
            await api.put(`/loans/${loan.id}`, { action, note, start_period: startPeriod });
            const label = LOAN_ACTION_LABEL[action]?.label || action;
            toast.success(`${label} complete`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        } finally {
            setBusyId("");
        }
    };

    // --- Rent receipt actions ---
    const rentAction = async (emp, action) => {
        let reason = "";
        if (action === "reject") {
            const r = await prompt({
                title: `Reject ${emp.employee_name}'s rent receipt?`,
                description: "The employee will be notified — share what needs to change.",
                placeholder: "e.g. Receipt is from a sub-letting agreement, not eligible",
                multiline: true,
                confirmLabel: "Reject receipt",
            });
            if (r === null) return;
            reason = r;
        } else {
            const ok = await confirm({
                title: `Approve ${emp.employee_name}'s rent receipt?`,
                description: "This activates the PAYE rent relief on future payroll runs.",
                confirmLabel: "Approve",
            });
            if (!ok) return;
        }
        setBusyId(emp.employee_id);
        try {
            await api.post(`/employees/${emp.employee_id}/rent-receipt/decision`, { action, reason });
            toast.success(`Rent receipt ${action}d`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        } finally {
            setBusyId("");
        }
    };

    const tabs = useMemo(() => ([
        { key: "leaves", label: "Leaves", icon: CalendarDays, count: data.totals?.leaves || 0 },
        { key: "loans", label: "Loans / Advances", icon: HandCoins, count: data.totals?.loans || 0 },
        { key: "rent_receipts", label: "Rent Receipts", icon: Receipt, count: data.totals?.rent_receipts || 0 },
    ]), [data.totals]);

    const canHrVerifyLeaves = hasRole(user, "admin", "hr");
    const canApproveRent = hasRole(user, "admin", "accountant");

    const [bulkBusy, setBulkBusy] = useState(false);
    const bulkApproveRent = async () => {
        const rows = data.rent_receipts || [];
        if (!rows.length) {
            toast.info("No pending rent receipts.");
            return;
        }
        const ok = await confirm({
            title: `Approve all ${rows.length} pending rent receipts?`,
            description: `Each employee's PAYE rent relief will activate immediately. Any DRAFT payroll runs will be recalculated automatically. Receipts without an uploaded file are skipped.`,
            confirmLabel: `Approve all (${rows.length})`,
        });
        if (!ok) return;
        setBulkBusy(true);
        try {
            const { data: res } = await api.post(`/rent-receipts/bulk-approve`, {});
            const a = res.approved_count || 0;
            const s = res.skipped_count || 0;
            const u = res.draft_payroll_items_updated || 0;
            toast.success(`${a} approved · ${s} skipped${u ? ` · ${u} payroll item${u > 1 ? "s" : ""} recalculated` : ""}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Bulk approve failed");
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="pending-approvals-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Approval Inbox</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Pending approvals
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Items waiting for your action across leaves, loans/advances, and rent receipt verifications.
                        Your role: <strong className="text-ink">{userRoles(user).join(", ") || "—"}</strong>.
                    </p>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    <button
                        onClick={load}
                        className="border border-ink/15 px-4 py-2.5 hover:bg-ink/5"
                        data-testid="pending-refresh-btn"
                    >
                        Refresh
                    </button>
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-klein/8 border border-klein/30 text-klein font-medium">
                        <Inbox className="w-4 h-4" />
                        <span data-testid="pending-total-count">{data.totals?.total || 0}</span>
                        <span className="opacity-70">items</span>
                    </div>
                </div>
            </div>

            <div className="mt-10 flex items-center gap-1 border-b border-ink/10">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px ${
                                tab === t.key
                                    ? "border-klein text-ink font-medium"
                                    : "border-transparent text-ink/50 hover:text-ink"
                            }`}
                            data-testid={`pending-tab-${t.key}`}
                        >
                            <Icon className="w-4 h-4" /> {t.label}
                            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 text-[11px] px-1.5 bg-ink/8 text-ink/70">
                                {t.count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-8" data-testid={`pending-panel-${tab}`}>
                {loading ? (
                    <div className="py-20 text-center text-sm text-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </div>
                ) : tab === "leaves" ? (
                    <LeavesPanel
                        leaves={data.leaves}
                        canHrVerify={canHrVerifyLeaves}
                        role={role}
                        isAdmin={hasRole(user, "admin")}
                        busyId={busyId}
                        onAction={leaveAction}
                    />
                ) : tab === "loans" ? (
                    <LoansPanel
                        loans={data.loans}
                        busyId={busyId}
                        onAction={loanAction}
                    />
                ) : (
                    <RentPanel
                        rows={data.rent_receipts}
                        busyId={busyId}
                        onAction={rentAction}
                        canApprove={canApproveRent}
                        bulkBusy={bulkBusy}
                        onBulkApprove={bulkApproveRent}
                    />
                )}
            </div>
        </div>
    );
}

function Pill({ map, status }) {
    const s = map[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[11px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}

function LeavesPanel({ leaves, canHrVerify, role, isAdmin, busyId, onAction }) {
    if (!leaves.length) {
        return <EmptyState icon={CalendarDays} text="No leaves awaiting your action." />;
    }
    return (
        <div className="bg-white border border-ink/10 overflow-auto">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b-2 border-ink/20">
                        <th className="text-left overline px-4 py-3">Employee</th>
                        <th className="text-left overline px-4 py-3">Type</th>
                        <th className="text-left overline px-4 py-3">Dates</th>
                        <th className="text-left overline px-4 py-3 hidden md:table-cell">Reason</th>
                        <th className="text-left overline px-4 py-3">Stage</th>
                        <th className="text-right overline px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {leaves.map((lv) => {
                        const busy = busyId === lv.id;
                        const cur = lv.status;
                        const canVerify = canHrVerify && cur === "pending";
                        // Dept lead approves at hr_verified; admin can also approve at hr_verified or pending
                        const canApprove = cur === "hr_verified" || (isAdmin && cur === "pending");
                        return (
                            <tr key={lv.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`pending-leave-row-${lv.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-medium text-ink">{lv.employee_name}</div>
                                    <div className="text-xs text-ink/50">
                                        {lv.employee_no || "—"} {lv.department ? `· ${lv.department}` : ""}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-ink/80">{lv.leave_type}</td>
                                <td className="px-4 py-3 text-ink/80">{lv.start_date} → {lv.end_date}</td>
                                <td className="px-4 py-3 text-ink/60 hidden md:table-cell max-w-sm truncate">{lv.reason || "—"}</td>
                                <td className="px-4 py-3"><Pill map={LEAVE_PILL} status={cur} /></td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                        {canVerify && (
                                            <ActionBtn
                                                testid={`leave-hr-verify-${lv.id}`}
                                                disabled={busy}
                                                onClick={() => onAction(lv, "hr_verify")}
                                                icon={ShieldCheck}
                                                cls="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                                            >
                                                HR Verify
                                            </ActionBtn>
                                        )}
                                        {canApprove && (
                                            <ActionBtn
                                                testid={`leave-approve-${lv.id}`}
                                                disabled={busy}
                                                onClick={() => onAction(lv, "approve")}
                                                icon={Check}
                                                cls="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                                            >
                                                Approve
                                            </ActionBtn>
                                        )}
                                        <ActionBtn
                                            testid={`leave-reject-${lv.id}`}
                                            disabled={busy}
                                            onClick={() => onAction(lv, "reject")}
                                            icon={Ban}
                                            cls="border-red-400 text-red-700 hover:bg-red-50"
                                        >
                                            Reject
                                        </ActionBtn>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function LoansPanel({ loans, busyId, onAction }) {
    if (!loans.length) {
        return <EmptyState icon={HandCoins} text="No loans or advances awaiting your action." />;
    }
    return (
        <div className="bg-white border border-ink/10 overflow-auto">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b-2 border-ink/20">
                        <th className="text-left overline px-4 py-3">Employee</th>
                        <th className="text-left overline px-4 py-3">Type</th>
                        <th className="text-right overline px-4 py-3">Amount</th>
                        <th className="text-right overline px-4 py-3 hidden lg:table-cell">Tenure</th>
                        <th className="text-left overline px-4 py-3">Stage</th>
                        <th className="text-right overline px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {loans.map((loan) => {
                        const busy = busyId === loan.id;
                        const acts = loan.available_actions || [];
                        return (
                            <tr key={loan.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`pending-loan-row-${loan.id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-medium text-ink">{loan.employee_name}</div>
                                    <div className="text-xs text-ink/50">
                                        {loan.employee_no || "—"} {loan.department ? `· ${loan.department}` : ""}
                                    </div>
                                    {loan.reason && <div className="text-xs text-ink/40 mt-0.5 truncate max-w-[260px]">{loan.reason}</div>}
                                </td>
                                <td className="px-4 py-3 text-ink/80 capitalize">{loan.type || "loan"}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{fmt(loan.amount)}</td>
                                <td className="px-4 py-3 text-right hidden lg:table-cell">
                                    {(loan.type || "loan") === "loan" ? `${loan.tenure_months || 1}m` : "Single"}
                                </td>
                                <td className="px-4 py-3"><Pill map={LOAN_PILL} status={loan.status} /></td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                        {acts.map((a) => {
                                            const cfg = LOAN_ACTION_LABEL[a];
                                            if (!cfg) return null;
                                            return (
                                                <ActionBtn
                                                    key={a}
                                                    testid={`loan-${a}-${loan.id}`}
                                                    disabled={busy}
                                                    onClick={() => onAction(loan, a)}
                                                    icon={cfg.icon}
                                                    cls={cfg.cls}
                                                >
                                                    {cfg.label}
                                                </ActionBtn>
                                            );
                                        })}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function RentPanel({ rows, busyId, onAction, canApprove, bulkBusy, onBulkApprove }) {
    if (!rows.length) {
        return <EmptyState icon={Receipt} text="No rent receipts awaiting verification." />;
    }
    return (
        <div>
            {canApprove && (
                <div className="mb-4 flex items-center justify-between flex-wrap gap-3 px-4 py-3 bg-emerald-50/70 border border-emerald-200 text-emerald-900 text-sm" data-testid="rent-bulk-approve-bar">
                    <span>
                        <strong>{rows.length}</strong> pending receipt{rows.length > 1 ? "s" : ""}. One click to activate PAYE rent relief for everyone with a valid uploaded receipt.
                    </span>
                    <button
                        onClick={onBulkApprove}
                        disabled={bulkBusy}
                        data-testid="rent-bulk-approve-btn"
                        className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5" />}
                        {bulkBusy ? "Approving…" : `Approve all (${rows.length})`}
                    </button>
                </div>
            )}
            <div className="bg-white border border-ink/10 overflow-auto">
            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b-2 border-ink/20">
                        <th className="text-left overline px-4 py-3">Employee</th>
                        <th className="text-right overline px-4 py-3">Annual rent</th>
                        <th className="text-left overline px-4 py-3 hidden md:table-cell">Receipt expiry</th>
                        <th className="text-left overline px-4 py-3 hidden md:table-cell">Uploaded</th>
                        <th className="text-right overline px-4 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((emp) => {
                        const busy = busyId === emp.employee_id;
                        return (
                            <tr key={emp.employee_id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`pending-rent-row-${emp.employee_id}`}>
                                <td className="px-4 py-3">
                                    <div className="font-medium text-ink">{emp.employee_name}</div>
                                    <div className="text-xs text-ink/50">
                                        {emp.employee_no || "—"} {emp.department ? `· ${emp.department}` : ""}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{fmt(emp.annual_rent)}</td>
                                <td className="px-4 py-3 text-ink/80 hidden md:table-cell">{emp.rent_receipt_expiry || "—"}</td>
                                <td className="px-4 py-3 text-ink/60 hidden md:table-cell">
                                    {emp.rent_receipt_uploaded_at ? new Date(emp.rent_receipt_uploaded_at).toLocaleDateString() : "—"}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                        <ActionBtn
                                            testid={`rent-approve-${emp.employee_id}`}
                                            disabled={busy}
                                            onClick={() => onAction(emp, "approve")}
                                            icon={FileSignature}
                                            cls="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                                        >
                                            Approve
                                        </ActionBtn>
                                        <ActionBtn
                                            testid={`rent-reject-${emp.employee_id}`}
                                            disabled={busy}
                                            onClick={() => onAction(emp, "reject")}
                                            icon={X}
                                            cls="border-red-400 text-red-700 hover:bg-red-50"
                                        >
                                            Reject
                                        </ActionBtn>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
        </div>
    );
}

function ActionBtn({ children, icon: Icon, cls, onClick, disabled, testid }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs uppercase tracking-[0.12em] border ${cls} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
            {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
            {children}
        </button>
    );
}

function EmptyState({ icon: Icon, text }) {
    return (
        <div className="py-20 text-center text-sm text-ink/40 border border-dashed border-ink/15 bg-white">
            <Icon className="w-8 h-8 mx-auto mb-3 text-ink/30" />
            {text}
        </div>
    );
}
